import { useMemo, useReducer } from 'react';

/**
 * Scene state + undo/redo for clip telestration.
 *
 * Object shape (coords in percent of video size, 0..1):
 *   { id, type, tIn, tOut, props }
 * types: 'circle' | 'rect' | 'line' | 'arrow' | 'text' | 'spotlight'
 * tIn/tOut: null = always visible (phase 1); numeric seconds in phase 2+
 */

const initial = {
  past: [],
  present: {
    version: 1,
    objects: [],
    selectedId: null,
    activeTool: 'select',
    color: '#FFD60A', // yellow
    strokeWidth: 3,
    defaultDuration: 3, // seconds — how long new annotations stay on screen
  },
  future: [],
};

const HISTORY_KEYS = ['objects']; // only object changes push history

function pushHistory(state, nextPresent) {
  const prevHistoryable = HISTORY_KEYS.some(
    (k) => state.present[k] !== nextPresent[k]
  );
  return {
    past: prevHistoryable ? [...state.past, state.present].slice(-50) : state.past,
    present: nextPresent,
    future: prevHistoryable ? [] : state.future,
  };
}

function reducer(state, action) {
  const p = state.present;

  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, present: { ...p, activeTool: action.tool, selectedId: null } };

    case 'SET_COLOR':
      return { ...state, present: { ...p, color: action.color } };

    case 'SET_STROKE_WIDTH':
      return { ...state, present: { ...p, strokeWidth: action.width } };

    case 'SET_DEFAULT_DURATION':
      return { ...state, present: { ...p, defaultDuration: action.seconds } };

    case 'ADD_OBJECT':
      return pushHistory(state, {
        ...p,
        objects: [...p.objects, action.object],
        selectedId: action.object.id,
        activeTool: action.keepTool ? p.activeTool : 'select',
      });

    case 'UPDATE_OBJECT':
      return pushHistory(state, {
        ...p,
        objects: p.objects.map((o) =>
          o.id === action.id ? { ...o, props: { ...o.props, ...action.props } } : o
        ),
      });

    case 'UPDATE_OBJECT_TIMING':
      return pushHistory(state, {
        ...p,
        objects: p.objects.map((o) =>
          o.id === action.id ? { ...o, tIn: action.tIn, tOut: action.tOut } : o
        ),
      });

    case 'UPSERT_KEYFRAME': {
      return pushHistory(state, {
        ...p,
        objects: p.objects.map((o) => {
          if (o.id !== action.id) return o;
          const keyframes = o.keyframes || [];
          const t = Math.round(action.t * 1000) / 1000;
          const TOL = 0.05;
          const existingIdx = keyframes.findIndex((k) => Math.abs(k.t - t) < TOL);
          if (existingIdx >= 0) {
            const next = keyframes.slice();
            next[existingIdx] = { ...next[existingIdx], props: { ...next[existingIdx].props, ...action.spatialProps } };
            return { ...o, keyframes: next };
          }
          const inserted = [...keyframes, { t, props: action.spatialProps }].sort((a, b) => a.t - b.t);
          return { ...o, keyframes: inserted };
        }),
      });
    }

    case 'DELETE_KEYFRAME': {
      return pushHistory(state, {
        ...p,
        objects: p.objects.map((o) => {
          if (o.id !== action.id) return o;
          const keyframes = (o.keyframes || []).filter((k) => Math.abs(k.t - action.t) > 0.05);
          return { ...o, keyframes };
        }),
      });
    }

    case 'DELETE_OBJECT': {
      const id = action.id || p.selectedId;
      if (!id) return state;
      return pushHistory(state, {
        ...p,
        objects: p.objects.filter((o) => o.id !== id),
        selectedId: p.selectedId === id ? null : p.selectedId,
      });
    }

    case 'SELECT_OBJECT':
      return { ...state, present: { ...p, selectedId: action.id } };

    case 'UNDO': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1];
      return {
        past: state.past.slice(0, -1),
        present: { ...previous, activeTool: p.activeTool, color: p.color, strokeWidth: p.strokeWidth },
        future: [p, ...state.future].slice(0, 50),
      };
    }

    case 'REDO': {
      if (state.future.length === 0) return state;
      const next = state.future[0];
      return {
        past: [...state.past, p],
        present: { ...next, activeTool: p.activeTool, color: p.color, strokeWidth: p.strokeWidth },
        future: state.future.slice(1),
      };
    }

    case 'LOAD_SCENE':
      return {
        past: [],
        present: { ...p, ...action.scene, selectedId: null },
        future: [],
      };

    default:
      return state;
  }
}

export function useTelestrationScene() {
  const [state, dispatch] = useReducer(reducer, initial);
  const p = state.present;

  const api = useMemo(() => ({
    scene: p,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    setTool: (tool) => dispatch({ type: 'SET_TOOL', tool }),
    setColor: (color) => dispatch({ type: 'SET_COLOR', color }),
    setStrokeWidth: (width) => dispatch({ type: 'SET_STROKE_WIDTH', width }),
    setDefaultDuration: (seconds) => dispatch({ type: 'SET_DEFAULT_DURATION', seconds }),
    addObject: (object, keepTool = false) => dispatch({ type: 'ADD_OBJECT', object, keepTool }),
    updateObject: (id, props) => dispatch({ type: 'UPDATE_OBJECT', id, props }),
    updateObjectTiming: (id, tIn, tOut) => dispatch({ type: 'UPDATE_OBJECT_TIMING', id, tIn, tOut }),
    upsertKeyframe: (id, t, spatialProps) => dispatch({ type: 'UPSERT_KEYFRAME', id, t, spatialProps }),
    deleteKeyframe: (id, t) => dispatch({ type: 'DELETE_KEYFRAME', id, t }),
    deleteObject: (id) => dispatch({ type: 'DELETE_OBJECT', id }),
    selectObject: (id) => dispatch({ type: 'SELECT_OBJECT', id }),
    undo: () => dispatch({ type: 'UNDO' }),
    redo: () => dispatch({ type: 'REDO' }),
    loadScene: (scene) => dispatch({ type: 'LOAD_SCENE', scene }),
  }), [p, state.past.length, state.future.length]);

  return api;
}

// Phase 1: always-visible objects. Phase 2 will gate by tIn/tOut vs currentTime.
export function isObjectVisibleAt(obj, currentTime) {
  if (obj.tIn == null && obj.tOut == null) return true;
  const tIn = obj.tIn ?? -Infinity;
  const tOut = obj.tOut ?? Infinity;
  return currentTime >= tIn && currentTime <= tOut;
}

let _idCounter = 0;
export function makeObjectId() {
  _idCounter += 1;
  return `obj_${Date.now().toString(36)}_${_idCounter}`;
}
